/**
 * The Popular Product Attributes card's views — "All" and one per product
 * category — built so the numbers add up: for every attribute, "All" is the
 * sum of the categories, and the "All" product count is the sum of the
 * category sizes.
 *
 * Two payload shapes reach here:
 *
 * - **Sized** (current workflow-service): the response carries
 *   `POPULAR_ATTRIBUTE_CATEGORIES` — every category of the run with its product
 *   count, including `other` (products with no category, or in a category
 *   with no curated attribute list). The batch-wide rows
 *   (`productCategory: null`) are not part of the card; every number comes
 *   from the category rows and those sizes.
 * - **Legacy** (no sizes): the old heuristics — largest `sampleSize` per
 *   category, the batch-wide row's `sampleSize` for "All". Kept so a run read
 *   from an older backend still renders.
 *
 * In both, a view hides attributes with `afterCount === 0` and is ordered by
 * `afterCount`, highest first.
 */

import type { AttributeItem } from '~/components/common/attribute-score-list';

/** One category of the card and its product count (`null` = unknown). */
export interface AttributeCategorySize {
  productCategory: string;
  cohortSize: number | null;
}

/** A dropdown option: a category and the number of products in it. */
export interface CategoryOption {
  slug: string;
  count: number;
}

export interface AttributeCategoryView {
  /** Rows to render for the selected view, zero rows hidden, sorted. */
  rows: AttributeItem[];
  /** Categories that have at least one row to show, in display order. */
  options: CategoryOption[];
  /** Products behind "All". */
  allCount: number;
  /** Products behind the selected view. */
  selectedCount: number;
}

/** The slug workflow-service folds uncategorized and uncurated products into. */
export const OTHER_CATEGORY = 'other';

/**
 * Match key for an attribute label — lower-cased, every run of
 * non-alphanumerics collapsed to one space. The same rule workflow-service and
 * integrations count attributes under, so "Fit type" and "Fit-Type" merge.
 */
export function attributeKey(label: string): string {
  return label
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Turns a taxonomy slug (e.g. `"apparel-and-accessories"`) into a display
 *  label ("Apparel & Accessories"). Done in JS rather than a CSS `capitalize`
 *  class: Chrome's `text-transform` sometimes skips the first letter of an
 *  inline element when it's preceded by a space living in a sibling text node
 *  (exactly the case for the subtitle's "in {category}." composition). */
export function humanizeCategory(slug: string): string {
  return slug
    .replaceAll(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replaceAll(/ And /g, ' & ');
}

/** Null-safe sum: stays `null` only when both sides are `null`. */
function sumPair(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

/** Highest `afterCount` first, unknown counts last, ties by title. */
function byAfterCountDesc(a: AttributeItem, b: AttributeItem): number {
  const left = a.afterCount ?? -1;
  const right = b.afterCount ?? -1;
  if (left !== right) return right - left;
  return a.title.localeCompare(b.title);
}

/** A view keeps an attribute unless it is known to cover no product after. */
function isShown(attr: AttributeItem): boolean {
  return attr.afterCount !== 0;
}

function score(count: number | null | undefined, size: number | undefined, maxScore: number) {
  return size ? ((count ?? 0) / size) * maxScore : 0;
}

/** One row per attribute key, counts summed across `rows`. */
function mergeByKey(rows: AttributeItem[], size: number | undefined): AttributeItem[] {
  const groups = new Map<string, AttributeItem[]>();
  for (const row of rows) {
    const key = attributeKey(row.title);
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  return [...groups.values()].map((group) => {
    const lead = group.reduce((best, row) =>
      (row.afterCount ?? -1) > (best.afterCount ?? -1) ? row : best,
    );
    const beforeCount = group.reduce<number | null>(
      (sum, row) => sumPair(sum, row.beforeCount),
      null,
    );
    const afterCount = group.reduce<number | null>(
      (sum, row) => sumPair(sum, row.afterCount),
      null,
    );
    const monthlySearches = group.reduce<number | null>(
      (max, row) => (row.monthlySearches == null ? max : Math.max(max ?? 0, row.monthlySearches)),
      null,
    );
    const maxScore = lead.maxScore ?? 10;
    return {
      title: lead.title,
      productCategory: null,
      maxScore,
      beforeCount,
      afterCount,
      monthlySearches,
      beforeScore: score(beforeCount, size, maxScore),
      afterScore: score(afterCount, size, maxScore),
    };
  });
}

function sizedView(
  attributes: AttributeItem[],
  sizes: AttributeCategorySize[],
  selected: string | null,
): AttributeCategoryView {
  const sizeOf = new Map(sizes.map((s) => [s.productCategory, s.cohortSize ?? undefined]));
  const allCount = sizes.reduce((sum, s) => sum + (s.cohortSize ?? 0), 0);
  // The batch-wide rows read different source columns and cannot be
  // reconciled with the categories, so a sized card never shows them.
  const categoryRows = attributes.filter((attr) => attr.productCategory != null);

  const rowsFor = (category: string) => {
    const size = sizeOf.get(category);
    return categoryRows
      .filter((attr) => attr.productCategory === category)
      .map((attr) => {
        const maxScore = attr.maxScore ?? 10;
        return {
          ...attr,
          beforeScore: score(attr.beforeCount, size, maxScore),
          afterScore: score(attr.afterCount, size, maxScore),
        };
      })
      .filter(isShown)
      .sort(byAfterCountDesc);
  };

  const options = sizes
    .filter((s) => rowsFor(s.productCategory).length > 0)
    .map((s) => ({ slug: s.productCategory, count: s.cohortSize ?? 0 }));

  if (selected === null) {
    return {
      rows: mergeByKey(categoryRows, allCount || undefined)
        .filter(isShown)
        .sort(byAfterCountDesc),
      options,
      allCount,
      selectedCount: allCount,
    };
  }
  return {
    rows: rowsFor(selected),
    options,
    allCount,
    selectedCount: sizeOf.get(selected) ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Legacy payloads (no POPULAR_ATTRIBUTE_CATEGORIES)
// ---------------------------------------------------------------------------

/** A category's size on a legacy payload: the largest `sampleSize` among its
 *  rows (the field never overcounts; it can undercount). */
function legacyCategorySize(attributes: AttributeItem[], category: string): number | undefined {
  const max = attributes.reduce((best, attr) => {
    if (attr.productCategory !== category || attr.sampleSize == null) return best;
    return Math.max(best, attr.sampleSize);
  }, 0);
  return max > 0 ? max : undefined;
}

/** "All" on a legacy payload: a batch-wide row's `sampleSize`, else the sum
 *  of the category sizes. */
function legacyAllSize(attributes: AttributeItem[], categories: string[]): number | undefined {
  const fromNullRow = attributes.find(
    (attr) => attr.productCategory == null && attr.sampleSize != null,
  )?.sampleSize;
  if (fromNullRow != null) return fromNullRow;
  const sizes = categories.map((category) => legacyCategorySize(attributes, category));
  if (sizes.every((size) => size == null)) return undefined;
  return sizes.reduce((sum: number, size) => sum + (size ?? 0), 0);
}

function legacyView(attributes: AttributeItem[], selected: string | null): AttributeCategoryView {
  const categories = [
    ...new Set(
      attributes
        .map((attr) => attr.productCategory)
        .filter((category): category is string => !!category),
    ),
  ];
  const allSize = legacyAllSize(attributes, categories);
  const rowsFor = (category: string) =>
    attributes
      .filter((attr) => attr.productCategory === category)
      .filter(isShown)
      .sort(byAfterCountDesc);

  const options = categories
    .filter((category) => rowsFor(category).length > 0)
    .map((slug) => ({ slug, count: legacyCategorySize(attributes, slug) ?? 0 }));

  if (selected !== null) {
    return {
      rows: rowsFor(selected),
      options,
      allCount: allSize ?? 0,
      selectedCount: legacyCategorySize(attributes, selected) ?? 0,
    };
  }

  // A label with category rows is their sum; a label with only a batch-wide
  // row passes through.
  const withCategories = new Set(
    attributes.filter((a) => a.productCategory != null).map((a) => attributeKey(a.title)),
  );
  const merged = mergeByKey(
    attributes.filter((attr) => attr.productCategory != null),
    allSize,
  );
  const batchOnly = attributes.filter(
    (attr) => attr.productCategory == null && !withCategories.has(attributeKey(attr.title)),
  );
  return {
    rows: [...merged, ...batchOnly].filter(isShown).sort(byAfterCountDesc),
    options,
    allCount: allSize ?? 0,
    selectedCount: allSize ?? 0,
  };
}

/** Whether the list has a category breakdown at all (and so a selector). */
export function hasCategories(attributes: AttributeItem[]): boolean {
  return attributes.some((attr) => attr.productCategory != null);
}

/**
 * The rows, dropdown options and product counts for one view of the card.
 * `selected === null` is "All".
 */
export function buildAttributeCategoryView(
  attributes: AttributeItem[],
  categorySizes: AttributeCategorySize[] | undefined,
  selected: string | null,
): AttributeCategoryView {
  return categorySizes && categorySizes.length > 0
    ? sizedView(attributes, categorySizes, selected)
    : legacyView(attributes, selected);
}

/**
 * The attributes the card's "All" view shows (or the list itself when it has
 * no categories) — for summaries that should agree with the card.
 */
export function popularAttributesShown(
  attributes: AttributeItem[],
  categorySizes: AttributeCategorySize[] | undefined,
): AttributeItem[] {
  if (!hasCategories(attributes)) return attributes.filter(isShown);
  return buildAttributeCategoryView(attributes, categorySizes, null).rows;
}
